/**
 * Main Salary Calculator Component
 * Provides bidirectional salary conversion between gross and net
 * Supports multiple professional statuses and work time configurations
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Calculator, Euro, TrendingUp, Info, Clock } from 'lucide-react';

// Import custom components
import SalaryInput from './SalaryInput';
import StatusSelector from './StatusSelector';
import WorkTimeSelector from './WorkTimeSelector';

// Import utilities and constants
import { calculateAllSalaryFields } from '../utils/salaryCalculations';
import { 
  WORKING_HOURS, 
  SOCIAL_CONTRIBUTION_RATES, 
  STATUS_LABELS, 
  SALARY_EXAMPLES, 
  SALARY_TIPS 
} from '../constants/salaryConstants';

/**
 * SalaryCalculator Component
 * 
 * Features:
 * - Bidirectional calculation (gross ↔ net)
 * - Multiple professional statuses
 * - Full-time and part-time support
 * - Real-time calculations
 * - Visual indicators for gross vs net
 */
const SalaryCalculator = () => {
  // State management
  const [salaryValues, setSalaryValues] = useState({
    horaireBrut: '',
    mensuelBrut: '',
    annuelBrut: '',
    horaireNet: '',
    mensuelNet: '',
    annuelNet: ''
  });

  const [status, setStatus] = useState('non_cadre');
  const [workTime, setWorkTime] = useState('temps_plein');
  const [partTimePercentage, setPartTimePercentage] = useState(100);
  const [isUpdating, setIsUpdating] = useState(false);

  /**
   * Handle salary field changes with bidirectional calculation
   * @param {string} field - The field that changed
   * @param {string} value - The new value
   */
  const handleSalaryChange = (field, value) => {
    // Clear all fields if input is empty
    if (!value || value === '' || value.trim() === '') {
      setSalaryValues({
        horaireBrut: '',
        mensuelBrut: '',
        annuelBrut: '',
        horaireNet: '',
        mensuelNet: '',
        annuelNet: ''
      });
    } else {
      // First update the changed field
      setSalaryValues(prev => ({
        ...prev,
        [field]: value
      }));
      
      // Then calculate all other fields
      setTimeout(() => {
        setIsUpdating(true);
        const calculatedValues = calculateAllSalaryFields(
          field, 
          value, 
          status, 
          workTime, 
          partTimePercentage
        );
        setSalaryValues(calculatedValues);
        setTimeout(() => setIsUpdating(false), 100);
      }, 50);
    }
  };

  /**
   * Recalculate all fields when status or work time changes
   */
  useEffect(() => {
    if (salaryValues.mensuelBrut && !isUpdating) {
      const calculatedValues = calculateAllSalaryFields(
        'mensuelBrut', 
        salaryValues.mensuelBrut, 
        status, 
        workTime, 
        partTimePercentage
      );
      setSalaryValues(calculatedValues);
    }
  }, [status, workTime, partTimePercentage]);

  /**
   * Get current deduction rates for selected status
   */
  const getCurrentDeductionRates = () => {
    const rates = SOCIAL_CONTRIBUTION_RATES[status];
    const totalRate = rates.cotisations_sociales + rates.csg_crds + rates.autres_deductions;
    return {
      ...rates,
      total: totalRate
    };
  };

  const deductionRates = getCurrentDeductionRates();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* SEO-optimized header */}
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3">
            <div className="bg-blue-600 p-2 rounded-lg" aria-hidden="true">
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
                Calcul du Salaire Net à Partir du Brut
              </h1>
              <p className="text-slate-600 text-sm sm:text-base mt-1">
                Simple et Instantané - France 2025
              </p>
            </div>
          </div>
          <p className="text-slate-600 mt-4 max-w-3xl">
            Convertisseur gratuit pour calculer votre salaire net à partir du brut instantanément. 
            Adapté à tous les statuts professionnels en France avec les cotisations sociales 2025.
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Main calculator - optimized for horizontal layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Salary Calculator Form */}
          <Card className="shadow-lg border-0 bg-white">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-lg py-3">
              <CardTitle className="flex items-center gap-2 text-slate-800 text-lg">
                <Euro className="w-5 h-5 text-blue-600" />
                Calculateur de Salaire
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {/* Salary input fields - compact layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Gross salary column */}
                <div className="space-y-3 p-4 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg border-2 border-blue-300 relative overflow-hidden shadow-md">
                  {/* Gross badge */}
                  <div className="absolute top-2 right-2 bg-blue-600 text-white px-2 py-1 rounded-full text-xs font-bold shadow-md" aria-label="Salaire brut">
                    BRUT
                  </div>
                  
                  {/* Decorative background patterns */}
                  <div className="absolute top-0 left-0 w-full h-full opacity-5" aria-hidden="true">
                    <div className="absolute top-1 left-1 w-6 h-6 bg-blue-600 rounded-full"></div>
                    <div className="absolute bottom-1 right-1 w-4 h-4 bg-blue-400 rounded-full"></div>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-2 relative z-10">
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-2 rounded-full shadow-md" aria-hidden="true">
                      <Calculator className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h2 className="font-bold text-blue-800 text-base">
                        Salaire BRUT
                      </h2>
                      <p className="text-xs text-blue-600 font-medium">Avant déductions</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 relative z-10">
                    <SalaryInput
                      id="horaireBrut"
                      label="Horaire BRUT (€/h)"
                      placeholder="Ex: 15.50"
                      value={salaryValues.horaireBrut}
                      onChange={(e) => handleSalaryChange('horaireBrut', e.target.value)}
                      icon={Clock}
                      emoji="💰"
                      type="brut"
                      size="small"
                    />
                    
                    <SalaryInput
                      id="mensuelBrut"
                      label="Mensuel BRUT (€)"
                      placeholder="Ex: 3000"
                      value={salaryValues.mensuelBrut}
                      onChange={(e) => handleSalaryChange('mensuelBrut', e.target.value)}
                      icon={Euro}
                      emoji="💰"
                      type="brut"
                      size="normal"
                    />
                    
                    <SalaryInput
                      id="annuelBrut"
                      label="Annuel BRUT (€)"
                      placeholder="Ex: 36000"
                      value={salaryValues.annuelBrut}
                      onChange={(e) => handleSalaryChange('annuelBrut', e.target.value)}
                      icon={TrendingUp}
                      emoji="💰"
                      type="brut"
                      size="small"
                    />
                  </div>
                </div>

                {/* Net salary column */}
                <div className="space-y-3 p-4 bg-gradient-to-br from-green-50 to-emerald-100 rounded-lg border-2 border-green-300 relative overflow-hidden shadow-md">
                  {/* Net badge */}
                  <div className="absolute top-2 right-2 bg-green-600 text-white px-2 py-1 rounded-full text-xs font-bold shadow-md" aria-label="Salaire net">
                    NET
                  </div>
                  
                  {/* Decorative background patterns */}
                  <div className="absolute top-0 left-0 w-full h-full opacity-5" aria-hidden="true">
                    <div className="absolute top-1 right-1 w-6 h-6 bg-green-600 rounded-full"></div>
                    <div className="absolute bottom-1 left-1 w-4 h-4 bg-green-400 rounded-full"></div>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-2 relative z-10">
                    <div className="bg-gradient-to-r from-green-600 to-green-700 p-2 rounded-full shadow-md" aria-hidden="true">
                      <TrendingUp className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h2 className="font-bold text-green-800 text-base">
                        Salaire NET
                      </h2>
                      <p className="text-xs text-green-600 font-medium">Après déductions</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 relative z-10">
                    <SalaryInput
                      id="horaireNet"
                      label="Horaire NET (€/h)"
                      placeholder="Ex: 12.30"
                      value={salaryValues.horaireNet}
                      onChange={(e) => handleSalaryChange('horaireNet', e.target.value)}
                      icon={Clock}
                      emoji="✅"
                      type="net"
                      size="small"
                    />
                    
                    <SalaryInput
                      id="mensuelNet"
                      label="Mensuel NET (€)"
                      placeholder="Ex: 2379"
                      value={salaryValues.mensuelNet}
                      onChange={(e) => handleSalaryChange('mensuelNet', e.target.value)}
                      icon={Euro}
                      emoji="✅"
                      type="net"
                      size="normal"
                    />
                    
                    <SalaryInput
                      id="annuelNet"
                      label="Annuel NET (€)"
                      placeholder="Ex: 28548"
                      value={salaryValues.annuelNet}
                      onChange={(e) => handleSalaryChange('annuelNet', e.target.value)}
                      icon={TrendingUp}
                      emoji="✅"
                      type="net"
                      size="small"
                    />
                  </div>
                </div>
              </div>

              {/* Status and work time selectors - compact horizontal layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                <StatusSelector 
                  value={status} 
                  onChange={setStatus} 
                />
                
                <WorkTimeSelector
                  workTime={workTime}
                  onWorkTimeChange={setWorkTime}
                  partTimePercentage={partTimePercentage}
                  onPartTimePercentageChange={setPartTimePercentage}
                />
              </div>
            </CardContent>
          </Card>

          {/* Calculation information - compact version */}
          <Card className="shadow-lg border-0 bg-white">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-t-lg py-3">
              <CardTitle className="flex items-center gap-2 text-slate-800 text-lg">
                <Info className="w-5 h-5 text-green-600" />
                Informations de Calcul
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-3">
                {/* Current status summary */}
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                  <h3 className="font-medium text-blue-800 mb-1 text-sm">Statut sélectionné</h3>
                  <p className="text-blue-700 text-sm font-medium">{STATUS_LABELS[status]}</p>
                  <div className="mt-2 text-xs text-blue-600 space-y-1">
                    <div className="flex justify-between">
                      <span>Cotisations sociales:</span>
                      <span className="font-medium">{(deductionRates.cotisations_sociales * 100).toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>CSG/CRDS:</span>
                      <span className="font-medium">{(deductionRates.csg_crds * 100).toFixed(1)}%</span>
                    </div>
                    {deductionRates.autres_deductions > 0 && (
                      <div className="flex justify-between">
                        <span>Autres déductions:</span>
                        <span className="font-medium">{(deductionRates.autres_deductions * 100).toFixed(1)}%</span>
                      </div>
                    )}
                    <div className="flex justify-between font-medium mt-1 pt-1 border-t border-blue-300">
                      <span>Total déductions:</span>
                      <span className="text-blue-800">{(deductionRates.total * 100).toFixed(1)}%</span>
                    </div>
                  </div>
                </div>

                {/* Calculation explanation */}
                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                  <h3 className="font-medium text-amber-800 mb-1 flex items-center gap-1 text-sm">
                    <Info className="w-3 h-3" />
                    Comment ça marche ?
                  </h3>
                  <ul className="text-amber-700 text-xs space-y-1" role="list">
                    <li>• Modifiez n'importe quel champ, les autres se calculent automatiquement</li>
                    <li>• Base de calcul: {WORKING_HOURS.HOURS_PER_WEEK}h/semaine, {WORKING_HOURS.HOURS_PER_YEAR}h/an</li>
                    <li>• Les taux sont approximatifs et basés sur 2025</li>
                    <li>• Pour un calcul précis, consultez un expert comptable</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Examples table */}
        <Card className="mt-8 shadow-lg border-0 bg-white">
          <CardHeader>
            <CardTitle className="text-slate-800">Exemples de Calculs (Mensuel)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full" role="table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th scope="col" className="text-left py-3 px-4 font-medium text-slate-700">Salaire Brut</th>
                    <th scope="col" className="text-left py-3 px-4 font-medium text-slate-700">Statut</th>
                    <th scope="col" className="text-left py-3 px-4 font-medium text-slate-700">Salaire Net Estimé</th>
                  </tr>
                </thead>
                <tbody>
                  {SALARY_EXAMPLES.map((example, index) => (
                    <tr key={index} className="border-b border-slate-100 hover:bg-slate-50 transition-colors duration-150">
                      <td className="py-3 px-4 font-medium">{example.brut} €</td>
                      <td className="py-3 px-4 text-slate-600">{example.status}</td>
                      <td className="py-3 px-4 font-medium text-green-700">{example.net} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Tips section */}
        <Card className="mt-8 shadow-lg border-0 bg-white">
          <CardHeader>
            <CardTitle className="text-slate-800">Conseils pour votre salaire</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3" role="list">
              {SALARY_TIPS.map((tip, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0" aria-hidden="true"></div>
                  <span className="text-slate-700">{tip}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </main>

      {/* SEO-optimized footer */}
      <footer className="bg-white border-t border-slate-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center space-y-2">
            <p className="text-sm text-slate-600">
              Données approximatives 2025. Non contractuel. © Le Salaire Net.fr
            </p>
            <p className="text-xs text-slate-500">
              Calculateur salaire brut net France - Cotisations sociales 2025 - 
              Conversion mensuel annuel horaire - Tous statuts professionnels
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SalaryCalculator;